import React, { useState, useRef, useEffect } from 'react'
import classNames from 'classnames'
import { escapeRegExp, isEmpty, isNil } from 'lodash'
import { IconSearch, IconCheck } from '@tabler/icons-react'
import pluralize from 'pluralize'

import { Button } from '@/components/buttons/Button'
import { Loader } from '@/components/loaders/Loader'
import { SearchableDropdownItem } from './SearchableDropdownItem'

const EDGE_KEYS = ['Home', 'End']
const ROW_NAVIGATION_KEYS = ['ArrowDown', 'ArrowUp', ...EDGE_KEYS]

export const SearchableDropdownResults = ({
  setOpen,
  options,
  handleSelect,
  loading,
  inputProps,
  hideSearch,
  selected,
  actions,
  pinnedAccessor,
  showResultsHeader,
  resultsHeader,
  handleCreate,
  fitContent,
  wide,
  alwaysOpen,
  isMultiSelect,
  emptyValue,
  isNestedSelect,
  isClearable,
  arrow,
}) => {
  const searchInputRef = useRef(null)
  const containerRef = useRef(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [creatableInput, setCreatableInput] = useState('')
  const hasSelections = isMultiSelect ? !isEmpty(selected) : !isNil(selected)

  const handleUpdateSearchQuery = (event) => {
    setSearchQuery(event.target.value)
  }

  const handleContainerKeyDown = (event) => {
    if (!ROW_NAVIGATION_KEYS.includes(event.key)) {
      return
    }

    // Home/End belong to the caret while the user is typing a search, so they
    // only jump to the first/last row once focus has moved into the list.
    if (event.target === searchInputRef.current && EDGE_KEYS.includes(event.key)) {
      return
    }

    const rows = Array.from(
      containerRef.current.querySelectorAll('[role="option"]:not([aria-disabled="true"])')
    )

    if (isEmpty(rows)) {
      return
    }

    event.preventDefault()

    const currentIndex = rows.indexOf(document.activeElement)
    const nextIndex = {
      ArrowDown: Math.min(currentIndex + 1, rows.length - 1),
      ArrowUp: Math.max(currentIndex - 1, 0),
      Home: 0,
      End: rows.length - 1,
    }[event.key]

    rows[nextIndex]?.focus()
  }

  const regex = new RegExp(escapeRegExp(searchQuery), 'i')
  const isMatch = (result) => regex.test(result)

  useEffect(() => {
    if (alwaysOpen || loading) {
      return
    }

    if (searchInputRef.current) {
      searchInputRef.current.focus({ preventScroll: true })
      return
    }

    // With no search input, focus never enters the dropdown on its own, which
    // would leave the arrow-key navigation unreachable. Focus the container
    // rather than the first option so no option reads as selected before the
    // user navigates
    containerRef.current?.focus({ preventScroll: true })
  }, [loading])

  let results
  if (isNestedSelect) {
    // For nested selects, filter each group and its nested options
    results = options
      .map((group) => {
        // Filter nested options by search query
        const filteredOptions = group.options.filter((nestedOption) =>
          !searchQuery ? true : isMatch(nestedOption.label)
        )
        if (filteredOptions.length > 0) {
          return { ...group, options: filteredOptions }
        }
        return null
      })
      .filter(Boolean)
  } else {
    results = options.filter((result) => (!searchQuery ? true : isMatch(result.label)))
  }

  const resultsCount = isNestedSelect
    ? results.reduce((count, group) => count + group.options.length, 0)
    : results.length

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      className={classNames('searchable-dropdown-container', {
        'fit-content': fitContent,
        wide,
        'always-open': alwaysOpen,
        'search-hidden': hideSearch,
      })}
      onKeyDown={handleContainerKeyDown}
    >
      {arrow}
      <div
        className={classNames({
          'searchable-dropdown-search': !isEmpty(actions) || !hideSearch,
          'with-actions': !isEmpty(actions),
        })}
      >
        {!hideSearch && (
          <div className="searchable-dropdown-search-input search-input">
            <input
              data-testid="searchable-dropdown-input"
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={handleUpdateSearchQuery}
              ref={searchInputRef}
              disabled={loading}
              {...inputProps}
            />
            <IconSearch />
          </div>
        )}
        {!isEmpty(actions) && (
          <div
            className={classNames('searchable-dropdown-actions', {
              'no-padding': hideSearch,
              'no-wrap': hideSearch,
            })}
          >
            {actions.map((action) => (
              <button
                key={action.key}
                data-testid={`searchable-dropdown-action-${action.key}`}
                type="button"
                className="searchable-dropdown-action"
                onClick={(event) => {
                  action.fn(event)

                  if (!alwaysOpen) {
                    setOpen(false)
                  }
                }}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {resultsHeader && !loading && (
        <div className="searchable-dropdown-results-note">{resultsHeader}</div>
      )}
      {showResultsHeader && !loading && !isEmpty(results) && (
        <header className="searchable-dropdown-results-header">
          <div className="flex-align-center small-gap">
            {isMultiSelect && (
              <>
                <span>{selected.length} selected</span>
                <small className="extra-muted-text"> | </small>
              </>
            )}
            <span>{pluralize(hideSearch ? 'item' : 'result', resultsCount, true)}</span>
          </div>
          <div>
            {hasSelections && isClearable && (
              <Button text onClick={() => handleSelect(emptyValue, emptyValue)}>
                Clear
              </Button>
            )}
          </div>
        </header>
      )}
      <div className="searchable-dropdown-menu">
        <div className="searchable-dropdown-results">
          {loading ? (
            <div className="searchable-dropdown-loading">
              <Loader data-testid="searchable-dropdown-loader" />
            </div>
          ) : !isEmpty(results) ? (
            <div className="searchable-dropdown-items">
              {isNestedSelect
                ? results.map((option) => (
                    <div className="searchable-dropdown-nested-group" key={option.label}>
                      <div className="searchable-dropdown-label">{option.label}</div>
                      {option.options.map((nestedOption) => (
                        <React.Fragment key={nestedOption.value}>
                          {nestedOption.hasSeparator && (
                            <div className="searchable-dropdown-divider" />
                          )}
                          <SearchableDropdownItem
                            option={nestedOption}
                            isMultiSelect={isMultiSelect}
                            handleSelect={handleSelect}
                            selected={selected}
                            pinnedAccessor={pinnedAccessor}
                          />
                        </React.Fragment>
                      ))}
                    </div>
                  ))
                : results.map((option) => (
                    <React.Fragment key={option.value}>
                      {option.hasSeparator && <div className="searchable-dropdown-divider" />}
                      <SearchableDropdownItem
                        option={option}
                        isMultiSelect={isMultiSelect}
                        handleSelect={handleSelect}
                        selected={selected}
                        pinnedAccessor={pinnedAccessor}
                      />
                    </React.Fragment>
                  ))}
            </div>
          ) : (
            <div className="searchable-dropdown-no-results">
              <p className="muted-text">No results</p>
            </div>
          )}
          {Boolean(handleCreate) && (
            <div className="creatable-input">
              <div className="creatable-input-search-input search-input">
                <input
                  className="searchable-dropdown-input"
                  type="text"
                  placeholder="Create new option..."
                  onChange={(event) => {
                    setCreatableInput(event.target.value)
                  }}
                />
              </div>
              {creatableInput && (
                <Button
                  brand
                  small
                  square
                  icon={<IconCheck />}
                  aria-label="Create option"
                  className="creatable-input-search-button"
                  onClick={() => {
                    handleCreate(creatableInput.trim())

                    if (!alwaysOpen) {
                      setOpen(false)
                    }
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
